import {
    Attachment,
    Avatar,
    Button,
    ButtonGroup,
    classNames,
    FDate,
    Header,
    Heading,
    Icon,
    Link,
    logger,
    ModalClose,
    Pagination,
    Pill,
    stopEvent,
    Text,
    Timeline,
    TimelineItem,
    Tooltip,
    View,
} from '@fold-dev/core'
import { useMemo, useState } from 'react'
import { TbAirBalloon, TbArchive, TbChecks, TbConfetti, TbX } from 'react-icons/tb'
import { useNavigate } from 'react-router'
import { Error, Loading } from '.'
import { NOTIFICATION_ACTION } from '../constants'
import { getDestination, getNotificationText } from '../helpers'
import { useNotifications } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'
import { PresenceAvatar } from './common/presence-avatar'

export const Notification = (props) => {
    const { notification, onMarkAsRead, onOpen, hideAttachments } = props
    const {
        id,
        action,
        read,
        createdAt,
        projectId,
        projectName,
        taskId,
        taskTitle,
        userId,
        userImage,
        userName,
        actorId,
        actorImage,
        actorName,
        taskComment,
        attachments,
    } = notification
    const date = useMemo(() => FDate(new Date(createdAt)).fromNow(), [createdAt])
    const text = useMemo(() => getNotificationText(notification), [notification])
    const className = classNames({
        'inbox-sidebar-notification': true,
        'read': read,
    })

    return (
        <TimelineItem
            onClick={onOpen}
            colorToken="text"
            className={className}
            marker={
                <PresenceAvatar
                    noPresence
                    size="sm"
                    src={actorImage}
                    name={actorName}
                />
            }>
            <View
                column
                gap="0.5rem"
                alignItems="flex-start">
                <View
                    width="100%"
                    position="relative">
                    <span
                        className="f-text inbox-text clamp-text-one"
                        dangerouslySetInnerHTML={{ __html: getDestination(notification) }}></span>
                </View>

                <Text
                    className="inbox-text"
                    size="sm">
                    {text}
                </Text>

                <div
                    className="f-row"
                    style={{ gap: 10, paddingBottom: '1rem' }}>
                    <Text
                        colorToken="text-weaker"
                        size="sm">
                        {date}
                    </Text>
                    {!read && (
                        <Link
                            size="sm"
                            colorToken="accent"
                            className="f-underline"
                            onClick={(e) => {
                                stopEvent(e)
                                onMarkAsRead(id)
                            }}>
                            Mark as read
                        </Link>
                    )}
                </div>

                {!!attachments && !hideAttachments && (
                    <View
                        row
                        width="100%"
                        gap="1rem"
                        justifyContent="flex-start">
                        {attachments.map(({ id, type, url, filesize, name }, index) => (
                            <Attachment
                                width={250}
                                key={index}
                                size="sm"
                                mime={type}
                                filesize={filesize}
                                label={name}
                                onClick={(e) => {
                                    stopEvent(e)
                                    window.open(url)
                                }}
                                className="f-buttonize-outline"
                            />
                        ))}
                    </View>
                )}
            </View>
        </TimelineItem>
    )
}

export const Inbox = ({ projectId, workspaceId, view }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const notificationsAPI = useNotifications()
    const navigate = useNavigate()
    const [page, setPage] = useState(1)
    const pageSize = 100
    const notifications = useAppStore((state) => state.notifications)
    const actions = useAppStore((state) => state.actions)
    const project = useAppStore((state) => state.project)
    const { projectNotifications, hasNotifications } = useMemo(() => {
        const projectNotifications = notifications[projectId] || []
        return { projectNotifications, hasNotifications: projectNotifications.some((n) => !n.read) }
    }, [projectId, notifications])
    const pagedNotifications = useMemo(() => {
        return projectNotifications //.filter((_, index: number) => index >= pageSize * (page - 1) && index < pageSize * (page - 1) + pageSize)
    }, [projectNotifications, pageSize, page])

    const handleMarkAllAsRead = async () => {
        setError(null)

        try {
            actions.setNotifications({
                ...notifications,
                [projectId]: projectNotifications.map((n) => (n.projectId == projectId ? { ...n, read: true } : n)),
            })
            await notificationsAPI.updateProject(workspaceId, projectId, { read: true })
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleMarkAsRead = async (notificationId) => {
        setError(null)

        try {
            actions.setNotifications({
                ...notifications,
                [projectId]: projectNotifications.map((n) => (n.id == notificationId ? { ...n, read: true } : n)),
            })
            await notificationsAPI.update(workspaceId, notificationId, { read: true })
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleRemoveAll = async () => {
        setError(null)

        try {
            actions.setNotifications({ ...notifications, [projectId]: [] })
            await notificationsAPI.removeProject(workspaceId, projectId)
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleOpen = async (notification) => {
        handleMarkAsRead(notification.id)

        switch (notification.action) {
            case NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION:
                if (!project.isChannel) actions.setSidebar('messages')
                break
            default:
                navigate(`/app/workspace/${workspaceId}/c/${projectId}/${view}/task/${notification.taskId}`)
                break
        }
    }

    return (
        <>
            <Header
                bgToken="sidebar"
                height="var(---header-sidebar-height)"
                p="0 1rem 0px 1rem">
                <View
                    width="100%"
                    position="relative"
                    row>
                    <Heading
                        as="h5"
                        style={{ textTransform: 'capitalize' }}>
                        Inbox
                    </Heading>
                    {!projectNotifications.length && <div className="f-flexer" />}
                    {!!projectNotifications.length && (
                        <>
                            <Pill
                                outline
                                size="sm"
                                m="0 auto 0 5px">
                                {projectNotifications.length}
                            </Pill>
                            <ButtonGroup>
                                {hasNotifications && (
                                    <Tooltip
                                        text="Mark all as read"
                                        anchor="bottom-center">
                                        <Button
                                            size="xs"
                                            bgToken="surface-strong"
                                            onClick={handleMarkAllAsRead}
                                            prefix={<Icon icon={TbChecks} />}>
                                            <span className="f-text sm">Mark all as Read</span>
                                        </Button>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    text="Archive all"
                                    anchor="bottom-center">
                                    <Button
                                        size="xs"
                                        bgToken="surface-strong"
                                        onClick={handleRemoveAll}>
                                        <Icon icon={TbArchive} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </>
                    )}
                    <Button
                        size="xs"
                        m="0 0 0 0.25rem"
                        bgToken="surface-strong"
                        onClick={() => actions.setSidebar(null)}>
                        <Icon
                            icon={TbX}
                            size="sm"
                        />
                    </Button>
                </View>
            </Header>

            <View
                position="relative"
                bgToken="sidebar"
                flex={1}
                width="100%"
                height="100%">
                <Loading loading={loading} />
                <Error error={error} />
                <div style={{ position: 'absolute', inset: 0, overflow: 'auto', height: '100%' }}>
                    {!pagedNotifications.length && (
                        <View
                            column
                            position="absolute"
                            style={{ inset: 0 }}
                            gap="1rem">
                            <Icon
                                icon={TbAirBalloon}
                                color="var(--f-color-surface-strongest)"
                                style={{
                                    '--f-icon-sizing-md': '7rem',
                                    '--f-icon-stroke-width-md': '0.75',
                                }}
                            />
                            <Heading
                                as="h4"
                                textAlign="center">
                                You're all caught up!
                            </Heading>
                            <Text textAlign="center">
                                You don't have any inbox notifications.
                            </Text>
                        </View>
                    )}

                    <View position="relative">
                        {!!pagedNotifications.length && (
                            <View width="100%">
                                <Timeline
                                    gap={0}
                                    style={{
                                        '--f-timeline-item-marker-line-color': 'var(--f-color-text-weakest)',
                                        '--f-timeline-item-margin': '0rem',
                                        '--f-timeline-item-marker-spacing': '1.5rem',
                                        '--f-timeline-item-marker-width': '2rem',
                                    }}>
                                    {pagedNotifications.map((notification, index) => (
                                        <Notification
                                            key={index}
                                            notification={notification}
                                            onMarkAsRead={handleMarkAsRead}
                                            onOpen={() => handleOpen(notification)}
                                            hideAttachments
                                        />
                                    ))}
                                </Timeline>
                            </View>
                        )}
                    </View>

                    {projectNotifications.length > pageSize && (
                        <View
                            row
                            p="2rem 0 1rem 0">
                            <Pagination
                                width="fit-content"
                                pageSize={pageSize}
                                totalCount={projectNotifications.length}
                                currentPage={page}
                                siblingCount={4}
                                separator="..."
                                onPageChange={setPage}
                            />
                        </View>
                    )}
                </div>
            </View>
        </>
    )
}
